// @flow
import React, {useState, useEffect, useRef} from 'react';
import {getConfigurationAdmin} from '../../../../utils/location';
import {useStoreCountry} from '../../../country/utils';

import {useShippingAddressRates} from '../../utils/use-shipping-address-rates';
import {
  makeStyles,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import {useGetCurrency, useShippingMethodConfig} from '../../utils';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {useFormContext, Controller} from 'react-hook-form';
import {DELIVERY_FORM_FIELDS} from './constant';
import type {ShippingAddress} from '../../../../entities';
import type {OrderCreationType} from '../../../../entities/order';

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
  shippingMethodItem: {
    display: 'flex',
    flexDirection: 'column',
    paddingBlock: '.5rem',
    borderTop: '1px solid #ccc',

    '&:first-child': {
      paddingTop: 0,
      borderTop: 'none',
    },
  },
  radioGroupContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  shippingMethodRadioContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  shippingMethodRadio: {
    flexGrow: 1,
  },
  subtitle: {
    fontWeight: 600,
    fontSize: '.875rem',
    color: colors.grey700,
    marginBottom: theme.spacing(1),
  },
  shippingMethodForm: {
    marginBottom: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  shippingMethodFormField: {
    display: 'flex',
    gap: '.5rem',
  },
  errorMessage: {
    color: colors.danger,
    fontSize: '0.75rem',
  },
}));

type Props = {
  defaultDeliveryAddress?: ShippingAddress,
  isEditing?: boolean,
  defaultCreationType?: OrderCreationType,
};

export default function ShippingMethod(props: Props) {
  const {defaultDeliveryAddress, isEditing} = props;
  const classes = useStyles();

  const {register, errors, setValue, watch} = useFormContext();

  const countryWatch = watch(
    DELIVERY_FORM_FIELDS.COUNTRY,
    defaultDeliveryAddress ? defaultDeliveryAddress?.country : ''
  );

  const isEditingOrDefaultAddress = isEditing;
  const country = useStoreCountry();
  const currency = useGetCurrency();
  const countryCode = country?.code || '';
  const [countryCodeSelected, setCountryCodeSelected] = useState(countryCode);
  const [shippingSelected, setShippingSelected] = useState(null);

  const defaultCountry =
    defaultDeliveryAddress && defaultDeliveryAddress?.country;

  const defaultAdmin1 = defaultDeliveryAddress && {
    adminId: defaultDeliveryAddress.admin1?.admin1Id,
    adminName: defaultDeliveryAddress.admin1?.admin1Name,
    type: 'admin1',
  };
  const defaultAdmin2 = defaultDeliveryAddress && {
    adminId: defaultDeliveryAddress.admin2?.admin2Id,
    adminName: defaultDeliveryAddress.admin2?.admin2Name,
    type: 'admin2',
  };
  const defaultAdmin3 = defaultDeliveryAddress && {
    adminId: defaultDeliveryAddress.admin3?.admin3Id,
    adminName: defaultDeliveryAddress.admin3?.admin3Name,
    type: 'admin3',
  };

  const {shippingMethod} = useShippingMethodConfig();
  const alreadySetCountry = useRef(false);
  const alreadySetAdmin1 = useRef(false);
  const alreadySetAdmin2 = useRef(false);
  const alreadySetAdmin3 = useRef(false);

  const {
    admin1List,
    admin2List,
    admin3List,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    shippingRates,
  } = useShippingAddressRates(
    countryWatch.code || countryCodeSelected || countryCode
  );

  const {admin1, admin2, admin3} = getConfigurationAdmin(
    countryWatch.code || countryCodeSelected
  );

  const handlePriceChange = event => {
    const price = Number(event.target.value);
    setValue(DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST, price);
  };

  useEffect(() => {
    if (shippingSelected) {
      setValue(
        DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST,
        Number(shippingSelected.price.split(' ')[1] || 0),
        {shouldValidate: true}
      );
    }
  }, [setValue, shippingSelected]);

  useEffect(() => {
    if (alreadySetCountry.current) return;
    if (defaultCountry) {
      setCountryCodeSelected(defaultCountry.code || countryCode);
      alreadySetCountry.current = true;
    }
  }, [countryCode, defaultCountry]);

  useEffect(() => {
    if (alreadySetAdmin1.current) return;
    if (defaultDeliveryAddress && admin1List.length > 0) {
      const adminId = defaultDeliveryAddress.admin1?.admin1Id.toString();
      if (adminId) {
        selectAdmin1(adminId);
        alreadySetAdmin1.current = true;
      }
    }
  }, [admin1List.length, defaultDeliveryAddress, selectAdmin1]);

  useEffect(() => {
    if (alreadySetAdmin2.current) return;
    if (defaultDeliveryAddress && admin2List.length > 0) {
      const adminId = defaultDeliveryAddress.admin2?.admin2Id.toString();
      if (adminId) {
        selectAdmin2(adminId);
        alreadySetAdmin2.current = true;
      }
    }
  }, [admin2List.length, defaultDeliveryAddress, selectAdmin2]);

  useEffect(() => {
    if (alreadySetAdmin3.current) return;
    if (defaultDeliveryAddress && admin3List.length > 0) {
      const adminId = defaultDeliveryAddress.admin3?.admin3Id.toString();
      if (adminId) {
        selectAdmin3(adminId);
        alreadySetAdmin3.current = true;
      }
    }
  }, [admin3List.length, defaultDeliveryAddress, selectAdmin3]);

  return (
    <div>
      <Typography className={classes.title}>Delivery</Typography>

      <Typography className={classes.subtitle}>
        1. Registra la dirección
      </Typography>
      <div className={classes.shippingMethodForm}>
        <CustomTextField
          label="Dirección"
          inputRef={register({required: 'Este campo es requerido'})}
          name={DELIVERY_FORM_FIELDS.ADDRESS_1}
          helperText={errors[DELIVERY_FORM_FIELDS.ADDRESS_1]?.message}
          error={!!errors[DELIVERY_FORM_FIELDS.ADDRESS_1]}
          defaultValue={defaultDeliveryAddress?.address1}
        />
        <CustomTextField
          label="Referencia (Opcional)"
          name={DELIVERY_FORM_FIELDS.ADDRESS_2}
          inputRef={register()}
          defaultValue={defaultDeliveryAddress?.address2}
        />
        <div className={classes.shippingMethodFormField}>
          <Controller
            name={DELIVERY_FORM_FIELDS.COUNTRY}
            defaultValue={defaultCountry}
            render={({onChange, onBlur, value, name, ref}) => (
              <CustomSelect
                native
                className={classes.selectWrapper}
                fullWidth
                label="País"
                value={countryCodeSelected}
                onChange={e => {
                  const value = e.target.value;
                  setCountryCodeSelected(value);
                  const country = shippingMethod?.availableShippingCountries.find(
                    country => country.code === value
                  );
                  onChange(country);
                }}
                helperText={errors[DELIVERY_FORM_FIELDS.COUNTRY]?.message}
                error={!!errors[DELIVERY_FORM_FIELDS.COUNTRY]}
                name={name}
              >
                <option value="" hidden />
                {shippingMethod?.availableShippingCountries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </CustomSelect>
            )}
          />

          {/*           <CustomTextField
            label="Código postal (opcional)"
            name={DELIVERY_FORM_FIELDS.ZIP_CODE}
            helperText={errors[DELIVERY_FORM_FIELDS.ZIP_CODE]?.message}
            error={!!errors[DELIVERY_FORM_FIELDS.ZIP_CODE]}
            inputRef={register()}
            defaultValue={defaultDeliveryAddress?.zipCode}
          /> */}
        </div>
        <Controller
          name={DELIVERY_FORM_FIELDS.ADMIN1}
          defaultValue={defaultAdmin1}
          rules={{required: 'Este campo es requerido'}}
          render={({onChange, onBlur, value}) => (
            <CustomSelect
              native
              className={classes.selectWrapper}
              fullWidth
              label={admin1.label}
              InputLabelProps={{shrink: selectedAdmin1?.adminId}}
              value={selectedAdmin1?.adminId}
              onChange={e => {
                const value = e.target.value;
                const adminAddress = admin1List.find(
                  admin => admin.adminId === value
                );
                onChange(adminAddress);
                selectAdmin1(value);
              }}
              helperText={errors[DELIVERY_FORM_FIELDS.ADMIN1]?.message}
              error={!!errors[DELIVERY_FORM_FIELDS.ADMIN1]}
              name="admin1"
            >
              <option value="" hidden />
              {admin1List?.map(admin => (
                <option key={admin.adminId} value={admin.adminId}>
                  {admin.adminName}
                </option>
              ))}
            </CustomSelect>
          )}
        />
        <Controller
          name={DELIVERY_FORM_FIELDS.ADMIN2}
          rules={{required: 'Este campo es requerido'}}
          defaultValue={defaultAdmin2}
          render={({onChange, onBlur, value}) => (
            <CustomSelect
              native
              className={classes.selectWrapper}
              fullWidth
              disabled={admin2List.length === 0}
              label={admin2.label}
              InputLabelProps={{shrink: selectedAdmin2?.adminId}}
              value={selectedAdmin2?.adminId}
              helperText={errors[DELIVERY_FORM_FIELDS.ADMIN2]?.message}
              error={!!errors[DELIVERY_FORM_FIELDS.ADMIN2]}
              onChange={e => {
                const value = e.target.value;
                const adminAddress = admin2List.find(
                  admin => admin.adminId === value
                );

                onChange(adminAddress);

                selectAdmin2(value);
              }}
              name="admin2"
            >
              <option value="" hidden />
              {admin2List?.map(admin => (
                <option key={admin.adminId} value={admin.adminId}>
                  {admin.adminName}
                </option>
              ))}
            </CustomSelect>
          )}
        />
        {admin3.enabled && (
          <Controller
            name={DELIVERY_FORM_FIELDS.ADMIN3}
            rules={{required: 'Este campo es requerido'}}
            defaultValue={defaultAdmin3}
            render={({onChange, onBlur, value}) => (
              <CustomSelect
                native
                className={classes.selectWrapper}
                fullWidth
                disabled={admin3List.length === 0}
                label={admin3.label}
                InputLabelProps={{shrink: selectedAdmin3?.adminId}}
                value={selectedAdmin3?.adminId}
                helperText={errors[DELIVERY_FORM_FIELDS.ADMIN3]?.message}
                error={!!errors[DELIVERY_FORM_FIELDS.ADMIN3]}
                onChange={e => {
                  const value = e.target.value;
                  const adminAddress = admin3List.find(
                    admin => admin.adminId === value
                  );

                  onChange(adminAddress);

                  selectAdmin3(value);
                }}
                name="admin3"
              >
                <option value="" hidden />
                {admin3List?.map(admin => (
                  <option key={admin.adminId} value={admin.adminId}>
                    {admin.adminName}
                  </option>
                ))}
              </CustomSelect>
            )}
          />
        )}
      </div>
      {shippingRates && shippingRates.length > 0 && (
        <div className={classes.shippingMethodRates}>
          <Typography className={classes.subtitle}>
            2. Tarifas de envío
          </Typography>
          <Controller
            name={DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID}
            rules={{required: 'Seleccione la tarifa de envío'}}
            render={({onChange, onBlur, value, name, ref}) => (
              <RadioGroup
                className={classes.radioGroupContainer}
                value={value}
                onChange={e => {
                  onChange(e.target.value);
                  if (isEditingOrDefaultAddress) {
                    setShippingSelected(
                      shippingRates.find(
                        shippingRate => shippingRate.id === e.target.value
                      )
                    );
                  }
                }}
                onBlur={onBlur}
              >
                {shippingRates.map(shippingRate => {
                  return (
                    <div
                      key={shippingRate.id}
                      className={classes.shippingMethodItem}
                    >
                      <div className={classes.shippingMethodRadioContainer}>
                        <FormControlLabel
                          value={shippingRate.id}
                          className={classes.shippingMethodRadio}
                          control={<Radio color="primary" />}
                          label={shippingRate.displayName}
                        />
                        <span className={classes.shippingMethodItemPrice}>
                          {shippingRate.price}
                        </span>
                      </div>

                      {shippingRate.description && (
                        <Typography variant="caption">
                          {shippingRate.description}
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            )}
          />

          {errors[DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID] && (
            <span className={classes.errorMessage}>
              {errors[DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID].message}
            </span>
          )}
        </div>
      )}
      {/* {isEditingOrDefaultAddress && <></>} */}
      <Typography className={classes.subtitle}>Precio Manual</Typography>
      <CustomTextField
        className={classes.textField}
        name={DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST}
        label="Precio de delivery"
        inputRef={register({
          required: isEditingOrDefaultAddress
            ? 'Este campo es requerido'
            : false,
        })}
        onChange={handlePriceChange}
        InputProps={{
          className: classes.textInput,
          startAdornment: (
            <InputAdornment position="start">
              {currency && currency.symbol}
            </InputAdornment>
          ),
        }}
        helperText={errors[DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST]?.message}
        error={!!errors[DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST]}
        InputLabelProps={{className: classes.textInputLabel}}
        type="number"
        inputProps={{step: '.1'}}
      />
    </div>
  );
}
